import { FormGroup, Intent, Button, Position, FormGroupProps } from '@blueprintjs/core'
import * as React from 'react'
import createFilterOptions from 'react-select-fast-filter-options'
import VirtualizedSelect from 'react-virtualized-select'
import useSWR from 'swr'
import * as Sentry from '@sentry/browser'
import Client from '../../client'
import { GutterTooltipError } from '../../routes/products_v5/components/JoinedForm/GutterTooltipError'
import {
  isRequiredError,
  renderError,
  renderErrorExceptRequired
} from '../../routes/products_v5/components/JoinedForm/Helpers'
import CHANNELS from '../../constants/Channels'

interface CategoryMarketCategoryPickerV2Props extends Omit<FormGroupProps, 'onChange'> {
  error?: string
  value: number
  marketId: number
  placeholder?: string
  simpleValue?: boolean
  showRequiredLabel?: boolean
  onChange: Function
  errorTooltipOffset?: number
}

const CategoryMarketCategoryPickerV2: React.FunctionComponent<CategoryMarketCategoryPickerV2Props> = ({
  onChange,
  ...props
}) => {
  const { data, isLoading, error, mutate } = useSWR(
    ['categoryMarketCategories', props.marketId],
    ([clientMethod, marketId]) => Client[clientMethod]({ marketId }),
    {
      onError: err => {
        Sentry.captureException(err)
      }
    }
  )

  const categories = React.useMemo(() => {
    // Skip processing if data is still loading
    if (isLoading) return []
    // Skip processing if there was an error
    if (error) return []
    // Skip processing if data is not in the expected format
    if (!data || !Array.isArray(data.data.data)) return []

    // Filter out deprecated categories for Etsy, unless the selected category is deprecated
    let payload = [...data.data.data]
    if (props.marketId === CHANNELS.ETSY.id) {
      payload = payload.filter(cmc => cmc.deprecatedById === null || cmc.id === props.value)
    }
    // Map the payload to the format expected by the select component
    return payload.map(c => ({ label: c.pathCache, value: c.id }))
  }, [data, isLoading, error, props.marketId, props.value])

  const filterOptions = React.useMemo(() => createFilterOptions({ options: categories }), [categories])
  const labelInfo = React.useMemo(() => (props.showRequiredLabel ? 'Required' : null), [props.showRequiredLabel])
  const isTooltipOpen = React.useMemo(() => typeof props.error === 'string' && props.error.length > 0, [props.error])

  return (
    <FormGroup // ProductFormGroup is tricky to use here
      style={{ maxWidth: 720, flex: 1 }}
      helperText={
        renderErrorExceptRequired(props.error, labelInfo) ||
        (error && (
          <Button onClick={mutate} minimal intent={Intent.DANGER} small>
            There was an error loading categories, click to try again.
          </Button>
        ))
      }
      className="float-right-formgroup-label"
      labelInfo={isRequiredError(props.error) ? renderError(labelInfo) : labelInfo}
      {...props}
    >
      <GutterTooltipError isOpen={isTooltipOpen} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <VirtualizedSelect
          disabled={props.disabled}
          className="long-select"
          onChange={onChange}
          value={props.value}
          isLoading={isLoading}
          labelKey="label"
          valueKey="value"
          placeholder={props.placeholder}
          simpleValue={props.simpleValue === undefined ? true : props.simpleValue}
          clearable
          filterOptions={typeof filterOptions === 'function' ? filterOptions : createFilterOptions({ options: [] })}
          options={categories}
        />
      </GutterTooltipError>
    </FormGroup>
  )
}

export default React.memo(CategoryMarketCategoryPickerV2)
